'use client';

import { useCallback, useEffect, useState } from 'react';
import './header.css';

declare global {
  interface Window {
    flutterMethod?: (method: string, ...args: any[]) => void;
  }
}

const MOBILE_MEDIA_QUERY = '(max-width: 768px)';

function setDesktopNavA11y(desktopNav: HTMLElement | null, isHidden: boolean) {
  if (!desktopNav) return;

  desktopNav.setAttribute('aria-hidden', String(isHidden));
  desktopNav.toggleAttribute('inert', isHidden);

  const focusableItems = desktopNav.querySelectorAll<HTMLElement>('a[href], button, [tabindex]');
  focusableItems.forEach((item) => {
    if (isHidden) {
      if (!item.hasAttribute('data-original-tabindex')) {
        item.setAttribute('data-original-tabindex', item.getAttribute('tabindex') ?? '');
      }
      item.setAttribute('tabindex', '-1');
      return;
    }

    const originalTabIndex = item.getAttribute('data-original-tabindex');
    if (originalTabIndex === null) return;

    if (originalTabIndex) {
      item.setAttribute('tabindex', originalTabIndex);
    } else {
      item.removeAttribute('tabindex');
    }
    item.removeAttribute('data-original-tabindex');
  });
}

function setMobileMenuA11y(
  mobileMenu: HTMLElement | null,
  menuToggle: HTMLButtonElement | null,
  isMobileViewport: boolean,
  isOpen: boolean,
) {
  const shouldExposeMenu = isMobileViewport && isOpen;

  if (mobileMenu) {
    mobileMenu.classList.toggle('open', shouldExposeMenu);
    mobileMenu.setAttribute('aria-hidden', String(!shouldExposeMenu));
    mobileMenu.toggleAttribute('inert', !shouldExposeMenu);

    const mobileItems = mobileMenu.querySelectorAll<HTMLElement>('[data-mobile-menu-item="true"], a[href], button');
    mobileItems.forEach((item) => {
      if (shouldExposeMenu) {
        item.removeAttribute('tabindex');
      } else {
        item.setAttribute('tabindex', '-1');
      }
    });
  }

  if (menuToggle) {
    menuToggle.setAttribute('aria-expanded', String(shouldExposeMenu));
    menuToggle.setAttribute('aria-label', shouldExposeMenu ? '关闭菜单' : '打开菜单');

    const menuToggleIcon = menuToggle.querySelector('span');
    if (menuToggleIcon) {
      menuToggleIcon.textContent = shouldExposeMenu ? '×' : '≡';
    }
  }

  document.body.style.overflow = shouldExposeMenu ? 'hidden' : '';
}

function hydrateMobileMenuNav() {
  const nav = document.getElementById('mobile-menu-nav');
  if (!nav || nav.getAttribute('data-hydrated') === 'true') return;

  const items = [
    {
      href: nav.dataset.homeHref,
      label: nav.dataset.homeLabel,
      active: nav.dataset.homeActive === 'true',
    },
    {
      href: nav.dataset.buyHref,
      label: nav.dataset.buyLabel,
      active: nav.dataset.buyActive === 'true',
    },
    {
      href: nav.dataset.esimHref,
      label: nav.dataset.esimLabel,
      active: nav.dataset.esimActive === 'true',
    },
    {
      href: nav.dataset.accountHref,
      label: nav.dataset.accountLabel,
      active: nav.dataset.accountActive === 'true',
    },
    { href: nav.dataset.blogHref, label: nav.dataset.blogLabel },
    { href: nav.dataset.helpHref, label: nav.dataset.helpLabel },
    { href: nav.dataset.appHref, label: nav.dataset.appLabel },
  ];

  items.forEach((item) => {
    if (!item.href || !item.label) return;

    const link = document.createElement('a');
    link.href = item.href;
    link.textContent = item.label;
    link.setAttribute('data-mobile-menu-item', 'true');
    link.setAttribute('tabindex', '-1');

    if (item.active) {
      link.classList.add('active');
      link.setAttribute('aria-current', 'page');
    }

    nav.appendChild(link);
  });

  nav.setAttribute('data-hydrated', 'true');
}

export const HeaderClient = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleMobileMenuToggle = useCallback(() => {
    setIsMobileMenuOpen((current) => !current);
  }, []);

  useEffect(() => {
    const menuToggle = document.getElementById('menu-toggle') as HTMLButtonElement | null;
    if (!menuToggle) return;

    menuToggle.addEventListener('click', handleMobileMenuToggle);

    return () => {
      menuToggle.removeEventListener('click', handleMobileMenuToggle);
    };
  }, [handleMobileMenuToggle]);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target || target.closest('[data-skip]')) return;

      const link = target.closest<HTMLAnchorElement>('a[href]');
      if (!link) return;

      if (target.closest('#mobile-menu')) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    const handleExternalClose = () => {
      setIsMobileMenuOpen(false);
    };

    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('yoho:mobile-menu-close', handleExternalClose);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('yoho:mobile-menu-close', handleExternalClose);
    };
  }, []);

  useEffect(() => {
    const desktopNav = document.querySelector<HTMLElement>('.desktop-nav');
    const mobileMenu = document.getElementById('mobile-menu');
    const menuToggle = document.getElementById('menu-toggle') as HTMLButtonElement | null;
    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);

    const syncHeaderA11y = () => {
      const isMobileViewport = mediaQuery.matches;

      hydrateMobileMenuNav();
      setDesktopNavA11y(desktopNav, isMobileViewport);
      setMobileMenuA11y(mobileMenu, menuToggle, isMobileViewport, isMobileMenuOpen);

      if (!isMobileViewport && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    syncHeaderA11y();

    mediaQuery.addEventListener?.('change', syncHeaderA11y);

    return () => {
      mediaQuery.removeEventListener?.('change', syncHeaderA11y);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  return null;
};
